
// Display model in millimetres. Numerical dimensions come from TB V1.2.
// Unspecified planar outlines are sampled from the matching original front views.
const TAU = Math.PI * 2;
const specs = [
  { id:'square-m', shape:'square', size:'M', x:-178, width:150, thickness:20, rod:13, length:220, straight:150, soft:70, base:115, height:17, upperR:4, lowerR:2, button:7, colour:0x226a4c, pantone:'3435C' },
  { id:'round-l', shape:'round', size:'L', x:0, width:200, thickness:28, rod:15.2, length:320, straight:220, soft:100, base:150, height:22, upperR:7, lowerR:3, button:9, colour:0xffad1c, pantone:'1235C' },
  { id:'triangle-s', shape:'triangle', size:'S', x:160, width:110, thickness:14, rod:11, length:150, straight:110, soft:40, base:75, height:14, upperR:3, lowerR:2, button:5, colour:0xf14523, pantone:'2028C' }
];

async function start(stage, host, signal) {
  const THREE = await import(host.dataset.threeUrl);
  const listen=(target,type,handler)=>target.addEventListener(type,handler,{signal});
  const profiles = await (await fetch(host.dataset.profilesUrl,{signal})).json();
  const logoTexture=await new THREE.TextureLoader().loadAsync(host.dataset.logoUrl);logoTexture.colorSpace=THREE.SRGBColorSpace;
  if(signal.aborted){logoTexture.dispose();return;}
  const logoMaterial=new THREE.MeshBasicMaterial({map:logoTexture,transparent:true,depthWrite:false,toneMapped:false});
  const renderer = new THREE.WebGLRenderer({alpha:true, antialias:true, powerPreference:'low-power'});
  renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
  renderer.setClearColor(0x000000, 0);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.0;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.domElement.setAttribute('aria-hidden','true');
  stage.append(renderer.domElement);
  const scene = new THREE.Scene();
  const camera = new THREE.OrthographicCamera(-400,400,300,-300,.1,5000);

  const studio = new THREE.Scene();
  studio.background = new THREE.Color(0xc9cbd0);
  function softbox(x,y,z,w,h,power) {
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(w,h),new THREE.MeshBasicMaterial({color:new THREE.Color().setScalar(power),side:THREE.DoubleSide}));
    mesh.position.set(x,y,z);mesh.lookAt(0,150,0);studio.add(mesh);
  }
  softbox(-430,520,450,140,620,4);
  softbox(430,390,250,100,550,2.5);
  softbox(0,650,-100,450,140,3);
  softbox(0,420,900,500,100,2.7);
  // Neutral black flags separate the clearcoat highlights without colour casts.
  const flagMaterial=new THREE.MeshBasicMaterial({color:0x17191b,side:THREE.DoubleSide});
  for(const x of [-600,600]){const flag=new THREE.Mesh(new THREE.PlaneGeometry(180,650),flagMaterial);flag.position.set(x,250,200);flag.lookAt(0,150,0);studio.add(flag);}
  const pmrem = new THREE.PMREMGenerator(renderer);
  const environment = pmrem.fromScene(studio,.025,1,1800);
  scene.environment = environment.texture;
  const fill = new THREE.HemisphereLight(0xffffff,0xe5e4e2,2.1);scene.add(fill);
  scene.add(new THREE.AmbientLight(0xffffff,.35));
  const key = new THREE.DirectionalLight(0xffffff,1.4);key.position.set(-250,950,500);scene.add(key);
  key.castShadow = true;key.shadow.mapSize.set(2048,2048);
  Object.assign(key.shadow.camera,{left:-500,right:500,top:650,bottom:-200,near:1,far:1800});
  key.shadow.normalBias=.6;key.shadow.bias=-.0004;key.shadow.radius=5;
  const rim=new THREE.DirectionalLight(0xffffff,1.0);rim.position.set(500,200,-350);scene.add(rim);

  // One shared material makes the three stems, bends and bases the same ivory.
  // This screen rendering is a visual reference; it is not a PANTONE conversion.
  const ivory = new THREE.MeshPhysicalMaterial({color:0xd8ceb5,roughness:.72,metalness:0,clearcoat:0,specularIntensity:.35,envMapIntensity:.25});
  // A small neutral studio matcap keeps the fixed, tiny chrome switch legible.
  // Its silver / dark reflection bands follow the surface normals, not the UI colour.
  const chromeCanvas=document.createElement('canvas');chromeCanvas.width=chromeCanvas.height=256;
  const chromeCtx=chromeCanvas.getContext('2d'),chromeGrad=chromeCtx.createLinearGradient(0,0,0,256);
  [[0,'#9aa5af'],[.32,'#edf1f4'],[.40,'#dce1e5'],[.475,'#dce1e5'],[.505,'#18212a'],[.53,'#5e6b76'],[.56,'#d9e2e8'],[.64,'#d8dfe4'],[1,'#a4afb9']].forEach(([p,c])=>chromeGrad.addColorStop(p,c));
  chromeCtx.fillStyle=chromeGrad;chromeCtx.fillRect(0,0,256,256);
  const chromeTexture=new THREE.CanvasTexture(chromeCanvas);chromeTexture.colorSpace=THREE.SRGBColorSpace;
  const chrome = new THREE.MeshMatcapMaterial({color:0xb5bcc3,matcap:chromeTexture,toneMapped:false});
  const diffuser = new THREE.MeshStandardMaterial({color:0xfffdf0,emissive:0xfff5d5,emissiveIntensity:.48,roughness:.65,metalness:0});
  const heads=[];

  function mesh(geometry,material,parent){const m=new THREE.Mesh(geometry,material);m.castShadow=true;m.receiveShadow=true;parent.add(m);return m;}
  function baseGeometry(s){
    const p=[new THREE.Vector2(0,0),new THREE.Vector2(s.base/2-s.lowerR,0)];
    for(let i=0;i<=12;i++){const a=-Math.PI/2+i/12*Math.PI/2;p.push(new THREE.Vector2(s.base/2-s.lowerR+s.lowerR*Math.cos(a),s.lowerR+s.lowerR*Math.sin(a)));}
    p.push(new THREE.Vector2(s.base/2,s.height-s.upperR));
    for(let i=0;i<=16;i++){const a=i/16*Math.PI/2;p.push(new THREE.Vector2(s.base/2-s.upperR+s.upperR*Math.cos(a),s.height-s.upperR+s.upperR*Math.sin(a)));}
    p.push(new THREE.Vector2(0,s.height));return new THREE.LatheGeometry(p,128);
  }
  // A short symmetric filter removes pixel stair steps from traced original outlines.
  // Restore their nominal extents after filtering; no new planar corner radius is introduced.
  function smoothOutline(raw){
    let points=raw.map(p=>[...p]);
    for(let pass=0;pass<3;pass++)points=points.map((p,i)=>[0,1].map(k=>{
      const n=points.length;return (points[(i+n-2)%n][k]+4*points[(i+n-1)%n][k]+6*p[k]+4*points[(i+1)%n][k]+points[(i+2)%n][k])/16;
    }));
    for(let k=0;k<2;k++){
      const lo=Math.min(...points.map(p=>p[k])),hi=Math.max(...points.map(p=>p[k]));
      const oldLo=Math.min(...raw.map(p=>p[k])),oldHi=Math.max(...raw.map(p=>p[k]));
      points.forEach(p=>p[k]=oldLo+(p[k]-lo)/(hi-lo)*(oldHi-oldLo));
    }return points;
  }
  function insetPolygon(points,d){
    return points.map((p,i)=>{
      const a=points[(i+points.length-1)%points.length],b=points[(i+1)%points.length];
      const n1=new THREE.Vector2(-(p.y-a.y),p.x-a.x).normalize();
      const n2=new THREE.Vector2(-(b.y-p.y),b.x-p.x).normalize();
      const n=n1.clone().add(n2).normalize();
      return p.clone().addScaledVector(n,d/Math.max(.28,n.dot(n1)));
    });
  }
  function headGeometry(points,thickness){
    const r=thickness/2,pos=[],idx=[],n=points.length,steps=24;
    for(let j=0;j<=steps;j++){
      const a=-Math.PI/2+Math.PI*j/steps;
      const ring=insetPolygon(points,r*(1-Math.cos(a)));
      const z=-r+r*Math.sin(a);
      ring.forEach(p=>pos.push(p.x,p.y,z));
    }
    for(let j=0;j<steps;j++)for(let i=0;i<n;i++){const a=j*n+i,b=j*n+(i+1)%n,c=(j+1)*n+i,d=(j+1)*n+(i+1)%n;idx.push(a,b,c,b,d,c);}
    const cap=insetPolygon(points,r);
    const tris=THREE.ShapeUtils.triangulateShape(cap,[]);
    const frontOffset=pos.length/3;cap.forEach(p=>pos.push(p.x,p.y,-thickness));
    const rearOffset=pos.length/3;cap.forEach(p=>pos.push(p.x,p.y,0));
    tris.forEach(t=>{idx.push(frontOffset+t[2],frontOffset+t[1],frontOffset+t[0]);idx.push(rearOffset+t[0],rearOffset+t[1],rearOffset+t[2]);});
    const g=new THREE.BufferGeometry();g.setAttribute('position',new THREE.Float32BufferAttribute(pos,3));g.setIndex(idx);g.setAttribute('uv',new THREE.Float32BufferAttribute(pos.flatMap((_,i)=>i%3===0?[pos[i]/200+.5,pos[i+1]/200+.5]:[]),2));g.computeVertexNormals();return g;
  }
  function glowTexture(){
    const c=document.createElement('canvas');c.width=c.height=256;const ctx=c.getContext('2d');
    const grad=ctx.createRadialGradient(128,128,0,128,128,128);grad.addColorStop(0,'rgba(255,236,170,0)');grad.addColorStop(.36,'rgba(255,236,170,0)');grad.addColorStop(.54,'rgba(255,236,170,.15)');grad.addColorStop(.67,'rgba(255,236,170,.05)');grad.addColorStop(1,'rgba(255,236,170,0)');ctx.fillStyle=grad;ctx.fillRect(0,0,256,256);return new THREE.CanvasTexture(c);
  }
  const haloTexture=glowTexture();
  // Fine base-coat texture underneath a smooth clearcoat. This is a display
  // material treatment, not a change to TB's specified paint or manufacturing.
  const flakeCanvas=document.createElement('canvas');flakeCanvas.width=flakeCanvas.height=256;
  const flakeCtx=flakeCanvas.getContext('2d'),flakeImage=flakeCtx.createImageData(256,256);let seed=7919;
  for(let i=0;i<flakeImage.data.length;i+=4){seed=(Math.imul(seed,1664525)+1013904223)>>>0;const v=116+(seed>>>27);flakeImage.data[i]=flakeImage.data[i+1]=flakeImage.data[i+2]=v;flakeImage.data[i+3]=255;}
  flakeCtx.putImageData(flakeImage,0,0);const flakeTexture=new THREE.CanvasTexture(flakeCanvas);
  flakeTexture.wrapS=flakeTexture.wrapT=THREE.RepeatWrapping;flakeTexture.repeat.set(9,9);flakeTexture.anisotropy=renderer.capabilities.getMaxAnisotropy();
  for(const s of specs){
    const lamp=new THREE.Group();lamp.position.x=s.x;lamp.name=s.id;scene.add(lamp);
    mesh(baseGeometry(s),ivory,lamp);
    // Flush chrome switch on the front vertical wall, never on the top face.
    const buttonRise=.55,buttonRadius=s.button/2,sphereRadius=(buttonRadius*buttonRadius+buttonRise*buttonRise)/(2*buttonRise);
    const buttonGeometry=new THREE.SphereGeometry(sphereRadius,64,24,0,TAU,0,Math.asin(buttonRadius/sphereRadius));buttonGeometry.translate(0,-sphereRadius+buttonRise,0);
    const button=mesh(buttonGeometry,chrome,lamp);
    button.rotation.x=Math.PI/2;button.position.set(0,s.height/2,s.base/2-.05);
    button.receiveShadow=false;button.castShadow=false;
    // Arc length is always the locked soft length. As the head lays flat,
    // the soft arc opens smoothly into a straight tangent, without stretching.
    class Stem extends THREE.Curve {
      constructor(){super();this.pitch=0;}
      getPoint(t,target=new THREE.Vector3()){
        const distance=t*s.length,theta=Math.PI/2-this.pitch;
        if(distance<=s.straight||theta<1e-6)return target.set(0,s.height+distance,0);
        const a=(distance-s.straight)/s.soft*theta,r=s.soft/theta;
        return target.set(0,s.height+s.straight+r*Math.sin(a),-r*(1-Math.cos(a)));
      }
      getTangent(t,target=new THREE.Vector3()){
        const a=Math.max(0,t*s.length-s.straight)/s.soft*(Math.PI/2-this.pitch);
        return target.set(0,Math.cos(a),-Math.sin(a));
      }
    }
    const path=new Stem(),segments=160,sides=40;
    const geometry=new THREE.BufferGeometry(),positions=new Float32Array((segments+1)*(sides+1)*3),normals=new Float32Array(positions.length),indices=[];
    for(let i=0;i<segments;i++)for(let j=0;j<sides;j++){
      const a=i*(sides+1)+j,b=a+1,c=a+sides+1,d=c+1;indices.push(a,c,b,b,c,d);
    }
    geometry.setAttribute('position',new THREE.BufferAttribute(positions,3));
    geometry.setAttribute('normal',new THREE.BufferAttribute(normals,3));geometry.setIndex(indices);
    const yaw=new THREE.Group();yaw.name='vertical-stem-yaw';lamp.add(yaw);
    const stem=mesh(geometry,ivory,yaw);stem.name='continuous-tangent-stem';stem.receiveShadow=false;
    const pivot=new THREE.Group();yaw.add(pivot);
    function poseStem(pitch){
      path.pitch=pitch;const p=new THREE.Vector3();
      for(let i=0;i<=segments;i++){
        const t=i/segments,a=Math.max(0,t*s.length-s.straight)/s.soft*(Math.PI/2-pitch);path.getPoint(t,p);
        for(let j=0;j<=sides;j++){
          const u=j/sides*TAU,nx=Math.cos(u),ny=Math.sin(u)*Math.sin(a),nz=Math.sin(u)*Math.cos(a),k=(i*(sides+1)+j)*3;
          positions[k]=p.x+s.rod/2*nx;positions[k+1]=p.y+s.rod/2*ny;positions[k+2]=p.z+s.rod/2*nz;
          normals[k]=nx;normals[k+1]=ny;normals[k+2]=nz;
        }
      }
      geometry.attributes.position.needsUpdate=true;geometry.attributes.normal.needsUpdate=true;geometry.computeBoundingSphere();
      pivot.position.copy(path.getPoint(1));pivot.rotation.x=pitch;
    }
    poseStem(0);
    const coat=new THREE.MeshPhysicalMaterial({color:s.colour,metalness:s.shape==='round'?.08:.24,roughness:.26,clearcoat:1,clearcoatRoughness:.055,specularIntensity:1,ior:1.52,envMapIntensity:1.35,bumpMap:flakeTexture,bumpScale:.018});
    const outline=s.shape==='round'?Array.from({length:192},(_,i)=>new THREE.Vector2(Math.cos(i/192*TAU)*s.width/2,Math.sin(i/192*TAU)*s.width/2)):smoothOutline(profiles[s.shape].outline).map(p=>new THREE.Vector2(...p));
    if(s.shape!=='round'){
      const xs=outline.map(p=>p.x),ys=outline.map(p=>p.y),minX=Math.min(...xs),maxX=Math.max(...xs),minY=Math.min(...ys),maxY=Math.max(...ys);
      outline.forEach(p=>{p.x=((p.x-minX)/(maxX-minX)-.5)*s.width;p.y=((p.y-minY)/(maxY-minY)-.5)*s.width;});
    }
    // Rear reference places the triangle connection below its bounding-box centre.
    if(s.shape==='triangle')outline.forEach(p=>p.y+=s.width*(68/476));
    const head=mesh(headGeometry(outline,s.thickness),coat,pivot);head.name='painted-metal-head';head.receiveShadow=false;
    // All three basic TB heads retain circular rear light rings.
    const ringOuter=s.width*(s.shape==='triangle'?.235:.292);
    const ringInner=s.width*(s.shape==='triangle'?.114:.145);
    const ring=mesh(new THREE.RingGeometry(ringInner,ringOuter,128),diffuser,pivot);ring.position.z=.12;
    const logoWidth=ringInner*.92;
    const rearLogo=mesh(new THREE.PlaneGeometry(logoWidth,logoWidth/(269/96)),logoMaterial,pivot);rearLogo.name='magemory-white-arc-rear-logo';rearLogo.position.set(0,ringInner*.55,.35);rearLogo.castShadow=false;
    const halo=mesh(new THREE.PlaneGeometry(ringOuter*3.1,ringOuter*3.1),new THREE.MeshBasicMaterial({map:haloTexture,transparent:true,depthWrite:false,side:THREE.FrontSide,toneMapped:false}),pivot);halo.position.z=.28;halo.castShadow=false;
    heads.push({yaw,pivot,stem,path,poseStem,spec:s});
  }
  const floor=mesh(new THREE.PlaneGeometry(2500,1800),new THREE.ShadowMaterial({color:0x63513e,opacity:.12}),scene);floor.rotation.x=-Math.PI/2;floor.position.y=-.08;floor.castShadow=false;

  let current=0,target=0,pitch=.22,targetPitch=.22,lastPitch=-1,raf=0,previous=0;
  const reduced=matchMedia('(prefers-reduced-motion: reduce)');
  function resize(){
    const {width:w,height:h}=stage.getBoundingClientRect();renderer.setSize(w,h,false);
    // Shared scale: never resize individual lamps to fabricate size differences.
    const viewHeight=Math.max(565,(w<600?600:660)/(w/h));
    camera.left=-viewHeight*w/h/2;camera.right=-camera.left;camera.top=viewHeight/2;camera.bottom=-camera.top;
    const framingY=w<600?220:265;camera.position.set(0,framingY+125,1500);camera.lookAt(0,framingY,0);camera.updateProjectionMatrix();draw();
  }
  function draw(){
    heads.forEach(({yaw,poseStem})=>{yaw.rotation.y=-.24+current;if(pitch!==lastPitch)poseStem(pitch);});
    lastPitch=pitch;
    scene.updateMatrixWorld(true);
    stage.dataset.pose=JSON.stringify(heads.map(({yaw,pivot,path,spec})=>{
      const end=yaw.localToWorld(path.getPoint(1));
      const centre=pivot.getWorldPosition(new THREE.Vector3());
      const up=new THREE.Vector3(0,1,0).transformDirection(pivot.matrixWorld);
      const incoming=path.getTangent(1).transformDirection(yaw.matrixWorld);
      const rear=new THREE.Vector3(0,0,1).transformDirection(pivot.matrixWorld);
      return {id:spec.id,axis:'Y',yaw:yaw.rotation.y,pitch:pivot.rotation.x,roll:pivot.rotation.z,
        connectionError:end.distanceTo(centre),upright:up.y,lightNormal:rear.toArray(),tangentAlignment:-incoming.dot(rear),headCentreY:centre.y};
    }));
    stage.dataset.angle=String(Math.round(current/TAU*360));
    stage.dataset.tilt=String(Math.round(pitch/Math.PI*180));
    renderer.render(scene,camera);
  }
  function frame(now){
    raf=0;
    if(signal.aborted)return;
    const dt=Math.min((now-previous)/1000,.05)||.016;previous=now;
    current+= (target-current)*(reduced.matches?1:1-Math.exp(-dt*10));
    pitch+=(targetPitch-pitch)*(reduced.matches?1:1-Math.exp(-dt*10));
    if(Math.abs(target-current)<.0002)current=target;
    if(Math.abs(targetPitch-pitch)<.0002)pitch=targetPitch;
    draw();if(current!==target||pitch!==targetPitch)raf=requestAnimationFrame(frame);
  }
  function wake(){if(!raf){previous=performance.now();raf=requestAnimationFrame(frame);}}
  function setAngle(deg,immediate=false){target=Math.max(0,Math.min(360,deg))/360*TAU;if(immediate){current=target;draw();}else wake();}
  function setTilt(deg,immediate=false){targetPitch=Math.max(0,Math.min(90,deg))/180*Math.PI;if(immediate){pitch=targetPitch;draw();}else wake();}
  const cursor=document.createElement('img');cursor.src=host.dataset.cursorUrl;cursor.alt='';cursor.className='tb-flower-cursor';cursor.setAttribute('aria-hidden','true');stage.append(cursor);
  function followPointer(event){
    stage.dataset.keyboard='false';
    if(event.pointerType==='touch'&&event.buttons===0)return;
    const rect=stage.getBoundingClientRect(),x=event.clientX-rect.left,y=event.clientY-rect.top;
    setAngle(x/rect.width*360);
    setTilt(Math.max(0,Math.min(1,(.65-y/rect.height)/.45))*90);
    stage.dataset.pointerGoal=JSON.stringify({yaw:target/TAU*360,tilt:targetPitch/Math.PI*180});
    if(event.pointerType!=='touch'){
      cursor.style.transform=`translate3d(${x-22}px,${y-22}px,0)`;cursor.dataset.visible='true';
    }
  }
  listen(stage,'pointermove',followPointer);
  listen(stage,'pointerdown',event=>{if(event.pointerType==='touch')stage.setPointerCapture(event.pointerId);followPointer(event);});
  listen(stage,'pointerleave',()=>{cursor.dataset.visible='false';});
  listen(stage,'blur',()=>{cursor.dataset.visible='false';});
  listen(stage,'keydown',event=>{
    if(event.key==='Escape'){stage.blur();cursor.dataset.visible='false';return;}
    if(!['ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home'].includes(event.key))return;
    event.preventDefault();stage.dataset.keyboard='true';
    if(event.key==='Home'){setAngle(0,true);setTilt(0,true);}
    if(event.key==='ArrowLeft')setAngle(Math.max(0,target/TAU*360-5),true);
    if(event.key==='ArrowRight')setAngle(Math.min(360,target/TAU*360+5),true);
    if(event.key==='ArrowUp')setTilt(Math.min(90,targetPitch/Math.PI*180+5),true);
    if(event.key==='ArrowDown')setTilt(Math.max(0,targetPitch/Math.PI*180-5),true);
  });
  const resizeObserver=new ResizeObserver(resize);resizeObserver.observe(stage);
  const intersectionObserver=new IntersectionObserver(entries=>{if(entries[0].isIntersecting)wake();else if(raf){cancelAnimationFrame(raf);raf=0;}},{rootMargin:'100px'});intersectionObserver.observe(stage);
  listen(document,'visibilitychange',()=>{if(!document.hidden)wake();else if(raf){cancelAnimationFrame(raf);raf=0;}});
  listen(renderer.domElement,'webglcontextlost',()=>{stage.dataset.render='fallback';});
  // Expose checked dimensions through DOM data, not a global application backdoor.
  stage.dataset.model=JSON.stringify(specs.map(({colour,...s})=>s));
  stage.dataset.motion='horizontal-yaw-and-bounded-downlight-tilt';
  signal.addEventListener('abort',()=>{
    cancelAnimationFrame(raf);resizeObserver.disconnect();intersectionObserver.disconnect();
    const materials=new Set(),textures=new Set();scene.traverse(object=>{object.geometry?.dispose();for(const material of (Array.isArray(object.material)?object.material:[object.material]))if(material)materials.add(material);});
    for(const material of materials){for(const value of Object.values(material))if(value?.isTexture)textures.add(value);material.dispose();}
    textures.forEach(t=>t.dispose());environment.dispose();pmrem.dispose();renderer.dispose();renderer.domElement.remove();cursor.remove();
  },{once:true});
  stage.dataset.render='ready';resize();draw();
}
// Each native theme block owns its listeners and renderer, including editor reloads.
if(!customElements.get('magemory-tb-hero'))customElements.define('magemory-tb-hero',class extends HTMLElement{
  connectedCallback(){
    if(this.controller)return;this.controller=new AbortController();
    const stage=this.querySelector('.tb-hero-stage');if(!stage)return;
    stage.dataset.render='loading';
    start(stage,this,this.controller.signal).catch(error=>{
      if(error.name==='AbortError'||!this.isConnected)return;
      stage.dataset.render='fallback';console.error('TB hero could not initialize:',error.message);
    });
  }
  disconnectedCallback(){this.controller?.abort();this.controller=null;}
});
